"use client"

import type { ReactNode } from "react"

import { ApprovalsTile } from "@/features/dashboard/home-d4/components/approvals-tile"
import { BirthdaysTile } from "@/features/dashboard/home-d4/components/birthdays-tile"
import { BudgetTile } from "@/features/dashboard/home-d4/components/budget-tile"
import { HomeTileSkeleton } from "@/features/dashboard/home-d4/components/home-tile"
import { MembersTile } from "@/features/dashboard/home-d4/components/members-tile"
import { NewMembersTile } from "@/features/dashboard/home-d4/components/new-members-tile"
import { RecentVisitorsTile } from "@/features/dashboard/home-d4/components/recent-visitors-tile"
import { SpendingTile } from "@/features/dashboard/home-d4/components/spending-tile"
import { TreasuryTile } from "@/features/dashboard/home-d4/components/treasury-tile"
import { useHomeD4Context } from "@/features/dashboard/home-d4/hooks/home-d4-context"
import type { HomeD4Data } from "@/features/dashboard/home-d4/hooks/use-home-d4-data"
import { HOME_D4_HERO_TILES, HomeTileId, HomeTileState } from "@/features/dashboard/home-d4/lib/home-grid-layout"
import { useCreateKindAccess } from "@/features/create-overlay/hooks/use-create-kind-access"
import { useCreateOverlay } from "@/features/create-overlay/hooks/use-create-overlay"
import { CreateKind } from "@/features/create-overlay/model/create-kind"
import { useTranslations } from "@/i18n"

/**
 * The fixed tile heights that make the engine's masonry rows line up like the D4 design: the hero row (treasury
 * and to validate) is taller, every other tile shares one height. A tile resized wider keeps its height.
 */
const HERO_HEIGHT_CLASS = "h-[19rem]"
const REGULAR_HEIGHT_CLASS = "h-[16.5rem]"

function TileFrame({ id, children }: { id: HomeTileId; children: ReactNode }) {
  return <div className={HOME_D4_HERO_TILES.has(id) ? HERO_HEIGHT_CLASS : REGULAR_HEIGHT_CLASS}>{children}</div>
}

/** Skeleton while loading, the tile once ready, nothing when absent (the registry hides absent tiles anyway). */
function HomeD4Widget({ id, render }: { id: HomeTileId; render: (data: HomeD4Data) => ReactNode }) {
  const data = useHomeD4Context()
  if (!data || data.states[id] === HomeTileState.Absent) return null
  return (
    <TileFrame id={id}>
      {data.states[id] === HomeTileState.Loading ? (
        <HomeTileSkeleton hero={id === HomeTileId.Treasury} testId={`home-skeleton-${id}`} />
      ) : (
        render(data)
      )}
    </TileFrame>
  )
}

export function HomeTreasuryWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <HomeD4Widget
      id={HomeTileId.Treasury}
      render={(data) =>
        data.finance ? (
          <TreasuryTile
            overview={data.finance}
            financeLink={{ label: t("treasury.link"), href: data.href("/finance") }}
            recordOffering={
              data.can.recordIncome && createAccess.isAllowed(CreateKind.Offering)
                ? { label: t("treasury.recordOffering"), href: hrefFor(CreateKind.Offering) }
                : null
            }
          />
        ) : null
      }
    />
  )
}

export function HomeApprovalsWidget() {
  return (
    <HomeD4Widget
      id={HomeTileId.Approvals}
      render={(data) =>
        data.approvals ? <ApprovalsTile summary={data.approvals} workQueueHref={data.href("/work-queue")} now={new Date()} /> : null
      }
    />
  )
}

export function HomeMembersWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <HomeD4Widget
      id={HomeTileId.Members}
      render={(data) =>
        data.members ? (
          <MembersTile
            overview={data.members}
            membersLink={{ label: t("members.link"), href: data.href("/members/list") }}
            addMember={data.can.editMembers && createAccess.isAllowed(CreateKind.Member) ? { label: t("members.add"), href: hrefFor(CreateKind.Member) } : null}
          />
        ) : null
      }
    />
  )
}

export function HomeRecentVisitorsWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <HomeD4Widget
      id={HomeTileId.RecentVisitors}
      render={(data) =>
        data.members ? (
          <RecentVisitorsTile
            visitors={data.members.recentVisitors.items}
            total={data.members.recentVisitors.total}
            visitorsLink={{ label: t("visitors.link"), href: data.href("/members/visitors") }}
            addVisitor={
              data.can.editMembers && createAccess.isAllowed(CreateKind.Visitor) ? { label: t("visitors.add"), href: hrefFor(CreateKind.Visitor) } : null
            }
          />
        ) : null
      }
    />
  )
}

export function HomeBirthdaysWidget() {
  return (
    <HomeD4Widget
      id={HomeTileId.Birthdays}
      render={(data) =>
        data.members ? (
          <BirthdaysTile birthdays={data.members.birthdaysThisWeek.items} total={data.members.birthdaysThisWeek.total} />
        ) : null
      }
    />
  )
}

export function HomeNewMembersWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <HomeD4Widget
      id={HomeTileId.NewMembers}
      render={(data) =>
        data.members ? (
          <NewMembersTile
            newMembers={data.members.newMembers}
            membersLink={{ label: t("newMembers.link"), href: data.href("/members/list") }}
            addMember={data.can.editMembers && createAccess.isAllowed(CreateKind.Member) ? { label: t("members.add"), href: hrefFor(CreateKind.Member) } : null}
          />
        ) : null
      }
    />
  )
}

export function HomeBudgetWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <HomeD4Widget
      id={HomeTileId.Budget}
      render={(data) =>
        data.budget ? (
          <BudgetTile
            overview={data.budget}
            budgetsLink={{ label: t("budget.link"), href: data.href("/budgets") }}
            createBudget={
              data.can.createBudget && createAccess.isAllowed(CreateKind.Budget)
                ? { label: t("budget.create"), href: hrefFor(CreateKind.Budget) }
                : null
            }
          />
        ) : null
      }
    />
  )
}

export function HomeSpendingWidget() {
  const t = useTranslations("dashboard.home")
  return (
    <HomeD4Widget
      id={HomeTileId.Spending}
      render={(data) =>
        data.finance ? (
          <SpendingTile overview={data.finance} detailLink={{ label: t("spending.link"), href: data.href("/finance/transactions") }} />
        ) : null
      }
    />
  )
}
